import { Link } from '@tanstack/react-router'
import { Table, Tag, Typography } from 'antd'
import {
  FfsLtaReport,
  FfsResultType,
  IndicationItem,
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
} from 'features/indicationTriage'
import React, { memo, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

const { Text } = Typography

const EmptyStateContainer = styled.div`
  padding: 2rem;
  text-align: center;
`

type FfsLevel = '1' | '2'
type FfsPart = 'part-4' | 'part-5'
type RepairRequiredType = 'Required' | 'Not Required'

interface InspectionFfsResultsTableProps {
  indications: readonly IndicationItem[]
  ffsReports: readonly GeckoEntry[]
  componentId?: string
}

interface FfsResultTableRow {
  key: string
  indicationId: string
  part4Level1Report: string
  part4Level1Result: FfsResultType
  part4Level2Report: string
  part4Level2Result: FfsResultType
  part5Level1Report: string
  part5Level1Result: FfsResultType
  part5Level2Report: string
  part5Level2Result: FfsResultType
  repairRequired: RepairRequiredType
}

const isFfsLtaReport = (
  entry: GeckoEntry
): entry is GeckoEntry & { data: FfsLtaReport } => {
  return entry.type === RolodexEntryType.FFS_LTA_REPORT
}

const getFfsDataFromEntry = (entry: GeckoEntry): FfsLtaReport | undefined => {
  if (!isFfsLtaReport(entry)) {
    return undefined
  }
  return entry.data
}

const getFfsResultForIndicationLevelAndPart = (
  indicationId: string,
  ffsReports: readonly GeckoEntry[],
  level: FfsLevel,
  part: FfsPart
): FfsLtaReport | undefined => {
  const matchingReports = ffsReports.filter((entry) => {
    if (!isFfsLtaReport(entry)) return false
    const data = entry.data
    return (
      entry.tags?.indication === indicationId &&
      data.ffs_level === level &&
      data.ffs_part === part
    )
  })

  const defaultReport = matchingReports.find((entry) => {
    if (!isFfsLtaReport(entry)) return false
    return entry.data.is_default === true
  })

  const selectedEntry = defaultReport ?? matchingReports[0]
  return selectedEntry ? getFfsDataFromEntry(selectedEntry) : undefined
}

/**
 * If any results were a fail, then repair is required
 * Otherwise, repair is not required
 */
const determineRepairRequired = (
  part4Level1Result: FfsResultType | undefined,
  part4Level2Result: FfsResultType | undefined,
  part5Level1Result: FfsResultType | undefined,
  part5Level2Result: FfsResultType | undefined
): RepairRequiredType => {
  if (
    part4Level1Result === FfsResultType.Fail ||
    part4Level2Result === FfsResultType.Fail ||
    part5Level1Result === FfsResultType.Fail ||
    part5Level2Result === FfsResultType.Fail
  ) {
    return 'Required'
  }

  return 'Not Required'
}

const InspectionFfsResultsTableComponent: React.FC<
  InspectionFfsResultsTableProps
> = ({ indications, ffsReports, componentId }) => {
  const columns = useMemo(
    () => [
      {
        title: 'Indication ID',
        dataIndex: 'indicationId',
        key: 'indicationId',
        render: (id: string) => {
          if (!componentId) return id
          return (
            <Link
              to='/details/$entryID'
              params={{ entryID: componentId }}
              search={{ extension: 'IndicationTriage', indication: id }}
              target='_blank'
              rel='noopener noreferrer'>
              {id}
            </Link>
          )
        },
      },

      {
        title: 'Part 4 L1 Result',
        dataIndex: 'part4Level1Result',
        key: 'part4Level1Result',
        render: (result: FfsResultType) => (
          <Tag color={RESULT_OUTCOME_COLORS[result] ?? 'blue'}>
            {RESULT_OUTCOME_LABELS[result] ?? result}
          </Tag>
        ),
      },

      {
        title: 'Part 4 L2 Result',
        dataIndex: 'part4Level2Result',
        key: 'part4Level2Result',
        render: (result: FfsResultType) => (
          <Tag color={RESULT_OUTCOME_COLORS[result] ?? 'blue'}>
            {RESULT_OUTCOME_LABELS[result] ?? result}
          </Tag>
        ),
      },

      {
        title: 'Part 5 L1 Result',
        dataIndex: 'part5Level1Result',
        key: 'part5Level1Result',
        render: (result: FfsResultType) => (
          <Tag color={RESULT_OUTCOME_COLORS[result] ?? 'blue'}>
            {RESULT_OUTCOME_LABELS[result] ?? result}
          </Tag>
        ),
      },

      {
        title: 'Part 5 L2 Result',
        dataIndex: 'part5Level2Result',
        key: 'part5Level2Result',
        render: (result: FfsResultType) => (
          <Tag color={RESULT_OUTCOME_COLORS[result] ?? 'blue'}>
            {RESULT_OUTCOME_LABELS[result] ?? result}
          </Tag>
        ),
      },
      {
        title: 'Repair Required',
        dataIndex: 'repairRequired',
        key: 'repairRequired',
        render: (status: RepairRequiredType) => {
          const color =
            status === 'Required'
              ? 'red'
              : status === 'Not Required'
                ? 'green'
                : 'gray'
          return <Tag color={color}>{status}</Tag>
        },
      },
    ],
    [componentId]
  )

  const tableData: FfsResultTableRow[] = useMemo(() => {
    return indications
      .map((indication) => {
        const part4Level1 = getFfsResultForIndicationLevelAndPart(
          indication.id,
          ffsReports,
          '1',
          'part-4'
        )
        const part4Level2 = getFfsResultForIndicationLevelAndPart(
          indication.id,
          ffsReports,
          '2',
          'part-4'
        )
        const part5Level1 = getFfsResultForIndicationLevelAndPart(
          indication.id,
          ffsReports,
          '1',
          'part-5'
        )
        const part5Level2 = getFfsResultForIndicationLevelAndPart(
          indication.id,
          ffsReports,
          '2',
          'part-5'
        )

        if (!part4Level1 && !part4Level2 && !part5Level1 && !part5Level2) {
          return null
        }

        return {
          key: indication.id,
          indicationId: indication.id,
          part4Level1Result:
            part4Level1?.outcome.result ?? FfsResultType.Pending,
          part4Level2Result:
            part4Level2?.outcome.result ?? FfsResultType.Pending,
          part5Level1Result:
            part5Level1?.outcome.result ?? FfsResultType.Pending,
          part5Level2Result:
            part5Level2?.outcome.result ?? FfsResultType.Pending,
          repairRequired: determineRepairRequired(
            part4Level1?.outcome.result,
            part4Level2?.outcome.result,
            part5Level1?.outcome.result,
            part5Level2?.outcome.result
          ),
        }
      })
      .filter((row): row is FfsResultTableRow => row !== null)
  }, [indications, ffsReports])

  if (tableData.length === 0) {
    return (
      <EmptyStateContainer>
        <Text type='secondary'>No FFS results found for this inspection</Text>
      </EmptyStateContainer>
    )
  }

  return (
    <Table
      dataSource={tableData}
      columns={columns}
      pagination={{ pageSize: 10, showSizeChanger: false }}
      size='small'
    />
  )
}

export const InspectionFfsResultsTable = memo(
  InspectionFfsResultsTableComponent
)
